import { useRef, useCallback } from "react";
import { ColumnKey } from "../types/workout-types";
import { useWorkoutNavigation } from "./useWorkoutNavigation";
import { useWorkoutHistory } from "./useWorkoutHistory";
import { useWorkoutEditing } from "./useWorkoutEditing";

export function useWorkoutActions(state: ReturnType<typeof import("./useWorkoutState").useWorkoutState>) {
  // Create refs
  const originalValueRef = useRef<string>("");
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  
  // Column mapping for keyboard navigation
  const columns: ColumnKey[] = ["exercise", "sets", "reps", "kgs", "rpe", "rest", "notes"];

  // Use specialized hooks
  const history = useWorkoutHistory(state.weeks, state.currentWeekIndex, state.currentDayIndex);
  
  const editing = useWorkoutEditing(
    state.weeks, 
    state.currentWeekIndex, 
    state.currentDayIndex, 
    state.activeCell,
    state.editMode,
    state.setActiveCell,
    state.setEditMode,
    state.setTempValue,
    state.tempValue,
    history.saveToHistory,
    state.setWeeks
  );
  
  const navigation = useWorkoutNavigation(
    state.currentWeekIndex,
    state.setCurrentWeekIndex,
    state.activeCell,
    state.setActiveCell,
    state.editMode,
    state.weeks.length
  );

  // Keyboard event handling
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => {
    if (!state.activeCell) return;

    const currentDay = state.weeks[state.currentWeekIndex]?.days[state.currentDayIndex];
    if (!currentDay) return;
    
    const exercise = currentDay.exercises[rowIndex];
    const field = columns[colIndex];
    const currentValue = exercise[field];

    if ((e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault();
      history.handleUndo();
      return;
    }

    if (((e.ctrlKey || e.metaKey) && e.key === "y") || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "z")) {
      e.preventDefault();
      history.handleRedo();
      return;
    }

    switch (e.key) {
      case "Enter":
        if (state.editMode) {
          editing.finishEditingWithSave(exercise.id, field, e.currentTarget.value);

          if (rowIndex < currentDay.exercises.length - 1) {
            state.setActiveCell({ rowIndex: rowIndex + 1, colIndex });
          }
        } else {
          editing.startEditing(rowIndex, colIndex, currentValue);
        }
        e.preventDefault();
        break;

      case "Escape":
        if (state.editMode) {
          editing.cancelEditing();
          e.preventDefault();
        }
        break;

      case "Tab":
        if (state.editMode) {
          editing.finishEditingWithSave(exercise.id, field, e.currentTarget.value);
        }

        let nextColIndex = colIndex;
        let nextRowIndex = rowIndex;

        if (!e.shiftKey) {
          nextColIndex++;
          if (nextColIndex >= columns.length) {
            nextColIndex = 0;
            nextRowIndex = Math.min(nextRowIndex + 1, currentDay.exercises.length - 1);
          }
        } else {
          nextColIndex--;
          if (nextColIndex < 0) {
            nextColIndex = columns.length - 1;
            nextRowIndex = Math.max(nextRowIndex - 1, 0);
          }
        }

        editing.startEditing(nextRowIndex, nextColIndex, currentDay.exercises[nextRowIndex][columns[nextColIndex]]);
        e.preventDefault();
        break;

      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
        if (!state.editMode || e.ctrlKey || e.metaKey) {
          if (state.editMode) {
            editing.finishEditingWithSave(exercise.id, field, e.currentTarget.value);
          }

          let newRowIndex = rowIndex;
          let newColIndex = colIndex;

          if (e.key === "ArrowUp") {
            newRowIndex = Math.max(rowIndex - 1, 0);
          } else if (e.key === "ArrowDown") {
            newRowIndex = Math.min(rowIndex + 1, currentDay.exercises.length - 1);
          } else if (e.key === "ArrowLeft") {
            newColIndex = Math.max(colIndex - 1, 0);
          } else if (e.key === "ArrowRight") {
            newColIndex = Math.min(colIndex + 1, columns.length - 1);
          }

          state.setActiveCell({ rowIndex: newRowIndex, colIndex: newColIndex });

          if (e.ctrlKey || e.metaKey) {
            editing.startEditing(newRowIndex, newColIndex, currentDay.exercises[newRowIndex][columns[newColIndex]]);
          }
          e.preventDefault();
        }
        break;
    }
  }, [state.activeCell, columns, state.currentDayIndex, state.currentWeekIndex, state.editMode, 
      editing.finishEditingWithSave, history.handleRedo, history.handleUndo, 
      state.setActiveCell, editing.startEditing, state.weeks, editing.cancelEditing]);

  return {
    originalValueRef,
    inputRefs,
    columns,
    saveToHistory: history.saveToHistory,
    handleUndo: history.handleUndo,
    handleRedo: history.handleRedo,
    handleCellChange: editing.handleCellChange,
    handleNotesChange: editing.handleNotesChange,
    handleCellClick: editing.handleCellClick,
    startEditing: editing.startEditing,
    finishEditingWithSave: editing.finishEditingWithSave,
    cancelEditing: editing.cancelEditing,
    handleKeyDown,
    handlePrevWeek: () => state.setCurrentWeekIndex(navigation.goToPreviousWeek()),
    handleNextWeek: () => state.setCurrentWeekIndex(navigation.goToNextWeek())
  };
}
